import { TimeValue } from '@gig/magic-common';
import { USER_TIMESTAMP_PREFERENCES_QUERY, USER_LOGGED_IN_UPDATED, USER_PREFERENCES_LOADING_UPDATED, USER_UPDATE_PREFERENCES_COMMAND, USER_RESET_PREFERENCES_COMMAND, USER_FETCH_PREFERENCES_COMMAND, USER_SHOW_GAMES_QUERY, USER_SHOW_SPORTSBOOK_QUERY, USER_UPDATE_PREFERENCES_UPDATED, USER_UPDATE_PREFERENCES_UPDATE_FAILED, USER_JURISDICTION_QUERY } from '@gig/magic-messages';
import { magic } from '@gig/magic-sdk';
import { Jurisdiction } from '@gig/magic-jurisdictions/dist/jurisdictions.constants';

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class UserPlayControlService {
  constructor() {
    this.playControlPreferences = null;
    this.timeStamp = new Date();
    this.loading = false;
    this.casinoQueryHandlerSubscription = null;
    this.sportsBookQueryHandlerSubscription = null;
    this.registerPreferencesQueryHandler();
    this.registerPreferencesUpdateHandler();
    this.registerFetchPreferencesHandler();
    this.registerPreferencesResetHandler();
    this.handleSessionChange();
  }
  sanitizePreference(preference, defaultValue) {
    return typeof preference === "boolean" ? preference : defaultValue;
  }
  registerPreferencesQueryHandler() {
    this.registerQueryHandlers();
    window.queryBus.subscribe(USER_TIMESTAMP_PREFERENCES_QUERY, () => {
      return this.timeStamp;
    });
  }
  handleSessionChange() {
    window.eventBus.subscribe(USER_LOGGED_IN_UPDATED, (e) => __async(this, null, function* () {
      const isLoggedIn = !!e.payload;
      const jurisdiction = yield window.queryBus.query(USER_JURISDICTION_QUERY);
      switch (jurisdiction) {
        case Jurisdiction.SGA:
          if (isLoggedIn) {
            yield this.loadUserPlayControlPreferences();
          }
          return;
        case Jurisdiction.LOTBA:
          if (isLoggedIn) {
            return this.loadUserPlayControlPreferences();
          } else {
            this.publishPreferences({
              showCasinoGames: true,
              showSportsbook: true,
              limitsConfirmed: false
            });
          }
          return;
        default:
          return;
      }
    }), { replay: true });
  }
  loadUserPlayControlPreferences() {
    return __async(this, null, function* () {
      this.timeStamp = new Date();
      this.unsubscribeQueries();
      try {
        this.loading = true;
        window.eventBus.publish(USER_PREFERENCES_LOADING_UPDATED, true);
        const preferences = yield magic.methods.users.getPreferences();
        this.publishPreferences(preferences);
      } catch (e) {
        console.log("unable to update preferences", e);
      } finally {
        this.loading = false;
        window.eventBus.publish(USER_PREFERENCES_LOADING_UPDATED, false);
        this.registerQueryHandlers();
      }
    });
  }
  getPlayControlPreferences() {
    return this.playControlPreferences ? this.playControlPreferences : {
      showCasinoGames: true,
      showSportsbook: true,
      limitsConfirmed: false
    };
  }
  registerPreferencesUpdateHandler() {
    window.commandBus.register(USER_UPDATE_PREFERENCES_COMMAND, {
      type: "object",
      properties: {
        showGames: {
          type: "boolean"
        },
        showSportsbook: {
          type: "boolean"
        },
        limitsConfirmed: {
          type: "boolean"
        }
      }
    });
    window.commandBus.subscribe(USER_UPDATE_PREFERENCES_COMMAND, (message) => {
      if (!message.payload) {
        return;
      }
      return this.doUpdateCall(message.payload);
    });
  }
  registerPreferencesResetHandler() {
    window.commandBus.subscribe(USER_RESET_PREFERENCES_COMMAND, () => {
      return this.doUpdateCall({
        showSportsbook: true,
        showCasinoGames: true,
        limitsConfirmed: false
      });
    });
  }
  registerFetchPreferencesHandler() {
    window.commandBus.subscribe(USER_FETCH_PREFERENCES_COMMAND, () => {
      const currentTime = new Date();
      if (currentTime.getTime() - this.timeStamp.getTime() > 10 * TimeValue.SECOND) {
        return this.loadUserPlayControlPreferences();
      }
    });
  }
  unsubscribeQueries() {
    var _a, _b;
    (_a = this.casinoQueryHandlerSubscription) == null ? void 0 : _a.unsubscribe();
    (_b = this.sportsBookQueryHandlerSubscription) == null ? void 0 : _b.unsubscribe();
    this.casinoQueryHandlerSubscription = null;
    this.sportsBookQueryHandlerSubscription = null;
  }
  registerQueryHandlers() {
    if (!this.casinoQueryHandlerSubscription) {
      this.casinoQueryHandlerSubscription = window.queryBus.subscribe(USER_SHOW_GAMES_QUERY, () => {
        var _a;
        return (_a = this.playControlPreferences) == null ? void 0 : _a.showCasinoGames;
      });
    }
    if (!this.sportsBookQueryHandlerSubscription) {
      this.sportsBookQueryHandlerSubscription = window.queryBus.subscribe(USER_SHOW_SPORTSBOOK_QUERY, () => {
        var _a;
        return (_a = this.playControlPreferences) == null ? void 0 : _a.showSportsbook;
      });
    }
  }
  publishPreferences(preferences) {
    const oldValues = this.getPlayControlPreferences();
    this.playControlPreferences = {
      showCasinoGames: this.sanitizePreference(preferences.showCasinoGames, oldValues.showCasinoGames),
      showSportsbook: this.sanitizePreference(preferences.showSportsbook, oldValues.showCasinoGames),
      limitsConfirmed: this.sanitizePreference(preferences.limitsConfirmed, false)
    };
    window.eventBus.publish(USER_UPDATE_PREFERENCES_UPDATED, {
      preferences: this.playControlPreferences,
      oldValues
    });
  }
  doUpdateCall(preferences) {
    return __async(this, null, function* () {
      try {
        yield magic.methods.users.setPreferences(preferences);
        this.publishPreferences(preferences);
      } catch (e) {
        console.error(e);
        window.eventBus.publish(USER_UPDATE_PREFERENCES_UPDATE_FAILED);
      }
    });
  }
}

export { UserPlayControlService };
